/* ═══════════════════════════════════════════════════════════════════════════════
   ORDER FLOW EXTENSION
   ───────────────────────────────────────────────────────────────────────────────
   Drop-in module that adds order flow indicators and momentum-continuation
   templates to StrategyBuilder. Designed to merge with existing CANDLE_INDICATORS
   and FUTURES_INDICATORS registries when `orderFlowEnabled` is true.

   IMPLEMENTATION NOTES (BACKEND):
   • Tier 1 indicators (Delta, CVD, Aggressor*, BookImbalance, Spread, large
     prints) are computable from public WS streams on Binance/Bybit/OKX/MEXC:
       - aggressor side from the trade stream (Binance: `m`, Bybit: `S`)
       - L2 imbalance from book depth snapshot + diffs
   • Tier 2 indicators (POC/VAH/VAL, CVD_Divergence, Absorption, VPIN) need
     additional aggregation passes — implement when needed.
   • MEXC's WS trade stream uses `T` for buyer-is-maker (inverse of Binance `m`).
     Verify aggressor classification per venue.
   ═══════════════════════════════════════════════════════════════════════════════ */


/* ═══════════════════════════════════════════════════════════════════════════════
   ORDER FLOW INDICATORS
   ═══════════════════════════════════════════════════════════════════════════════ */

export const ORDER_FLOW_INDICATORS = {

  // ── Cumulative Volume Delta ─────────────────────────────────────
  CVD:            { id: 'CVD',            label: 'CVD',              cat: 'Order Flow', params: [] },
  CVD_EMA:        { id: 'CVD_EMA',        label: 'CVD EMA',          cat: 'Order Flow', params: [{ n: 'period', d: 14, min: 2, max: 200 }] },
  CVD_Divergence: { id: 'CVD_Divergence', label: 'CVD Divergence',   cat: 'Order Flow', params: [{ n: 'lookback', d: 20, min: 5, max: 200 }] },

  // ── Per-bar Delta ───────────────────────────────────────────────
  Delta:          { id: 'Delta',          label: 'Delta',            cat: 'Order Flow', params: [] },
  DeltaPercent:   { id: 'DeltaPercent',   label: 'Delta %',          cat: 'Order Flow', params: [], range: [-100, 100] },
  DeltaMomentum:  { id: 'DeltaMomentum',  label: 'Delta Momentum',   cat: 'Order Flow', params: [{ n: 'period', d: 5, min: 1, max: 50 }] },
  MaxDelta:       { id: 'MaxDelta',       label: 'Max Delta',        cat: 'Order Flow', params: [{ n: 'lookback', d: 20, min: 1, max: 200 }] },
  MinDelta:       { id: 'MinDelta',       label: 'Min Delta',        cat: 'Order Flow', params: [{ n: 'lookback', d: 20, min: 1, max: 200 }] },

  // ── Aggressor / Trade Side ──────────────────────────────────────
  AggressorRatio:    { id: 'AggressorRatio',    label: 'Aggressor Ratio', cat: 'Aggressor', params: [{ n: 'period', d: 100, min: 10, max: 1000 }], range: [0, 1] },
  AggressorRatioEMA: { id: 'AggressorRatioEMA', label: 'Aggressor EMA',   cat: 'Aggressor', params: [{ n: 'period', d: 50,  min: 5,  max: 500  }], range: [0, 1] },
  BuyMarketVolume:   { id: 'BuyMarketVolume',   label: 'Buy Mkt Vol',     cat: 'Aggressor', params: [{ n: 'period', d: 20,  min: 1,  max: 500  }] },
  SellMarketVolume:  { id: 'SellMarketVolume',  label: 'Sell Mkt Vol',    cat: 'Aggressor', params: [{ n: 'period', d: 20,  min: 1,  max: 500  }] },
  SellVolumeChange:  { id: 'SellVolumeChange',  label: 'Sell Vol Δ %',    cat: 'Aggressor', params: [{ n: 'period', d: 5,   min: 1,  max: 100  }] },
  BuyVolumeChange:   { id: 'BuyVolumeChange',   label: 'Buy Vol Δ %',     cat: 'Aggressor', params: [{ n: 'period', d: 5,   min: 1,  max: 100  }] },
  TradeCount:        { id: 'TradeCount',        label: 'Trade Count',     cat: 'Aggressor', params: [{ n: 'period', d: 60,  min: 1,  max: 1000 }] },
  AvgTradeSize:      { id: 'AvgTradeSize',      label: 'Avg Trade Size',  cat: 'Aggressor', params: [{ n: 'period', d: 60,  min: 1,  max: 1000 }] },

  // ── Order Book ──────────────────────────────────────────────────
  BookImbalance:  { id: 'BookImbalance',  label: 'Book Imbalance',   cat: 'Order Book', params: [{ n: 'depth',  d: 10, min: 1, max: 50 }],  range: [-1, 1] },
  BidAskRatio:    { id: 'BidAskRatio',    label: 'Bid/Ask Ratio',    cat: 'Order Book', params: [{ n: 'depth',  d: 10, min: 1, max: 50 }] },
  Spread:         { id: 'Spread',         label: 'Spread (bps)',     cat: 'Order Book', params: [] },
  SpreadEMA:      { id: 'SpreadEMA',      label: 'Spread EMA',       cat: 'Order Book', params: [{ n: 'period', d: 20, min: 2, max: 200 }] },
  BookDepth:      { id: 'BookDepth',      label: 'Book Depth',       cat: 'Order Book', params: [{ n: 'levels', d: 10, min: 1, max: 50 }] },
  BookSlope:      { id: 'BookSlope',      label: 'Book Slope',       cat: 'Order Book', params: [{ n: 'levels', d: 20, min: 5, max: 100 }] },

  // ── Volume Profile ──────────────────────────────────────────────
  POC:            { id: 'POC',            label: 'POC',              cat: 'Volume Profile', params: [{ n: 'lookback', d: 100, min: 20, max: 1000 }] },
  VAH:            { id: 'VAH',            label: 'VAH',              cat: 'Volume Profile', params: [{ n: 'lookback', d: 100, min: 20, max: 1000 }] },
  VAL:            { id: 'VAL',            label: 'VAL',              cat: 'Volume Profile', params: [{ n: 'lookback', d: 100, min: 20, max: 1000 }] },
  HVN_Distance:   { id: 'HVN_Distance',   label: 'HVN Distance %',   cat: 'Volume Profile', params: [{ n: 'lookback', d: 100, min: 20, max: 1000 }] },
  LVN_Distance:   { id: 'LVN_Distance',   label: 'LVN Distance %',   cat: 'Volume Profile', params: [{ n: 'lookback', d: 100, min: 20, max: 1000 }] },

  // ── Whale / Large Print Detection ───────────────────────────────
  LargeTradeBuy:  { id: 'LargeTradeBuy',  label: 'Large Buy',        cat: 'Whales', params: [{ n: 'minSize', d: 100000, min: 1000, max: 10000000 }, { n: 'period', d: 60, min: 1, max: 1000 }] },
  LargeTradeSell: { id: 'LargeTradeSell', label: 'Large Sell',       cat: 'Whales', params: [{ n: 'minSize', d: 100000, min: 1000, max: 10000000 }, { n: 'period', d: 60, min: 1, max: 1000 }] },
  WhaleNetFlow:   { id: 'WhaleNetFlow',   label: 'Whale Net Flow',   cat: 'Whales', params: [{ n: 'period', d: 50, min: 5, max: 500 }, { n: 'minSize', d: 100000, min: 1000, max: 10000000 }] },

  // ── Microstructure ──────────────────────────────────────────────
  Absorption:       { id: 'Absorption',       label: 'Absorption',        cat: 'Microstructure', params: [{ n: 'lookback', d: 5,  min: 1, max: 50 }, { n: 'minDelta', d: 1000, min: 1, max: 10000000 }], range: [0, 1] },
  StackedImbalance: { id: 'StackedImbalance', label: 'Stacked Imbalance', cat: 'Microstructure', params: [{ n: 'levels',  d: 3,  min: 2, max: 10 }, { n: 'ratio',    d: 3,    min: 1.5, max: 10 }] },
  LiquiditySweep:   { id: 'LiquiditySweep',   label: 'Liq Sweep',         cat: 'Microstructure', params: [{ n: 'lookback', d: 20, min: 5, max: 200 }] },
  VPIN:             { id: 'VPIN',             label: 'VPIN',              cat: 'Microstructure', params: [{ n: 'buckets', d: 50, min: 10, max: 500 }], range: [0, 1] },
  KyleLambda:       { id: 'KyleLambda',       label: 'Kyle λ',            cat: 'Microstructure', params: [{ n: 'period', d: 100, min: 20, max: 1000 }] },

  // ── Consolidation / Range Detection ─────────────────────────────
  // Useful for the "wait for consolidation after big move" pattern
  ConsolidationRange: { id: 'ConsolidationRange', label: 'Consol Range %', cat: 'Range', params: [{ n: 'period',     d: 10, min: 3,  max: 100 }] },
  ConsolidationLow:   { id: 'ConsolidationLow',   label: 'Consol Low',     cat: 'Range', params: [{ n: 'period',     d: 10, min: 3,  max: 100 }] },
  ConsolidationHigh:  { id: 'ConsolidationHigh',  label: 'Consol High',    cat: 'Range', params: [{ n: 'period',     d: 10, min: 3,  max: 100 }] },
  PriorBarMove:       { id: 'PriorBarMove',       label: 'Prior Move %',   cat: 'Range', params: [{ n: 'lookback',   d: 5,  min: 1,  max: 50  }] },
};


/* ═══════════════════════════════════════════════════════════════════════════════
   ORDER FLOW TEMPLATES — SPOT
   ───────────────────────────────────────────────────────────────────────────────
   The first three templates are the honest spot version of the
   "post-pump consolidation + kill the wrong side" idea. The bracket-entry
   property is enforced by the runtime (limit-buy at consol low + take-profit
   limit-sell at consol high). The "kill the wrong side" mechanic becomes a
   conditional close logic: if order flow confirms continuation, cancel the
   take-profit and switch to trailing stop. That switch is a runtime concern,
   but the conditions that trigger it are defined here.
   ═══════════════════════════════════════════════════════════════════════════════ */

export const ORDER_FLOW_SPOT_TEMPLATES = [
  {
    name: 'Post-Pump Consolidation',
    desc: 'Buy consolidation low after a 30%+ move, take profit at consol high',
    buy: [
      { ind: 'PriorBarMove',       params: { lookback: 5 },           op: '>',  val: '30' },
      { ind: 'ConsolidationRange', params: { period: 10 },            op: '<',  val: '8',  combinator: 'AND' },
      { ind: 'Price',                                                  op: '<=', cmp: 'ConsolidationLow', cmpParams: { period: 10 }, combinator: 'AND' },
      { ind: 'CVD',                                                    op: '>',  val: '0',  combinator: 'AND' },
    ],
    sell: [
      { ind: 'Price', op: '>=', cmp: 'ConsolidationHigh', cmpParams: { period: 10 } },
    ],
  },

  {
    name: 'Momentum Continuation Trigger',
    desc: 'Cancel TP and ride continuation when order flow confirms — pair with trailing stop',
    buy: [
      { ind: 'Price',           op: '>',  cmp: 'ConsolidationHigh', cmpParams: { period: 10 } },
      { ind: 'BuyVolumeChange', params: { period: 5 }, op: '>', val: '10', combinator: 'AND' },
      { ind: 'AggressorRatio',  params: { period: 100 }, op: '>', val: '0.6', combinator: 'AND' },
    ],
    sell: [
      // Exit on flow flip — this is the "kill the wrong side" inverted for spot
      { ind: 'SellVolumeChange', params: { period: 5 },   op: '>', val: '10' },
      { ind: 'AggressorRatio',   params: { period: 100 }, op: '<', val: '0.45', combinator: 'OR' },
    ],
  },

  {
    name: 'CVD-Confirmed Breakout',
    desc: 'Only chase breakouts when delta confirms — filter out fakeouts',
    buy: [
      { ind: 'Price',          op: '>', cmp: 'EMA', cmpParams: { period: 20 } },
      { ind: 'CVD_EMA',        params: { period: 14 },  op: '>', val: '0',    combinator: 'AND' },
      { ind: 'DeltaMomentum',  params: { period: 5 },   op: '>', val: '0',    combinator: 'AND' },
      { ind: 'AggressorRatio', params: { period: 100 }, op: '>', val: '0.55', combinator: 'AND' },
    ],
    sell: [
      { ind: 'CVD_Divergence', params: { lookback: 20 }, op: '<', val: '0' },
      { ind: 'DeltaMomentum',  params: { period: 5 },    op: '<', val: '0',  combinator: 'OR' },
    ],
  },

  {
    name: 'Whale-Followed Long',
    desc: 'Enter on sustained large-print buying with price confirmation',
    buy: [
      { ind: 'WhaleNetFlow',  params: { period: 50, minSize: 100000 }, op: '>', val: '0' },
      { ind: 'LargeTradeBuy', params: { minSize: 100000, period: 60 }, op: '>', val: '0', combinator: 'AND' },
      { ind: 'Price',                                                   op: '>', cmp: 'VWAP',                  combinator: 'AND' },
    ],
    sell: [
      { ind: 'LargeTradeSell', params: { minSize: 100000, period: 60 }, op: '>', val: '0' },
      { ind: 'WhaleNetFlow',   params: { period: 50, minSize: 100000 }, op: '<', val: '0', combinator: 'OR' },
    ],
  },

  {
    name: 'Aggressor Flip Reversal',
    desc: 'Fade exhausted moves — extreme aggressor ratio reverses',
    buy: [
      { ind: 'AggressorRatio', params: { period: 100 }, op: '<', val: '0.25' },
      { ind: 'RSI',            params: { period: 14 },  op: '<', val: '30',   combinator: 'AND' },
    ],
    sell: [
      { ind: 'AggressorRatio', params: { period: 100 }, op: '>', val: '0.6' },
    ],
  },

  {
    name: 'Absorption Bounce',
    desc: 'Heavy sell delta + flat price = sellers exhausted, buy the bounce',
    buy: [
      { ind: 'Absorption', params: { lookback: 5, minDelta: 1000 }, op: '>', val: '0.7' },
      { ind: 'Delta',                                                op: '<', val: '0',   combinator: 'AND' },
      { ind: 'Price',                                                op: '>=', cmp: 'VWAP', combinator: 'AND' },
    ],
    sell: [
      { ind: 'Delta', op: '<', val: '0' },
      { ind: 'CVD',   op: '<', cmp: 'CVD_EMA', cmpParams: { period: 14 }, combinator: 'AND' },
    ],
  },

  {
    name: 'Liquidity Sweep + CVD',
    desc: 'Stop hunt below recent low followed by delta-confirmed reversal',
    buy: [
      { ind: 'LiquiditySweep', params: { lookback: 20 }, op: '<', val: '0' },
      { ind: 'CVD',                                       op: '>', val: '0', combinator: 'AND' },
      { ind: 'AggressorRatio', params: { period: 100 },   op: '>', val: '0.55', combinator: 'AND' },
    ],
    sell: [
      { ind: 'CVD',           op: '<', cmp: 'CVD_EMA', cmpParams: { period: 14 } },
    ],
  },

  {
    name: 'POC Magnet (Mean Reversion)',
    desc: 'Trade back toward Point of Control — clean flow only (low VPIN)',
    buy: [
      { ind: 'Price', op: '<', cmp: 'POC', cmpParams: { lookback: 100 } },
      { ind: 'VPIN',  params: { buckets: 50 }, op: '<', val: '0.3',   combinator: 'AND' },
      { ind: 'CVD',                            op: '>', val: '0',     combinator: 'AND' },
    ],
    sell: [
      { ind: 'Price', op: '>=', cmp: 'POC', cmpParams: { lookback: 100 } },
    ],
  },
];


/* ═══════════════════════════════════════════════════════════════════════════════
   ORDER FLOW TEMPLATES — FUTURES (one-way mode)
   ───────────────────────────────────────────────────────────────────────────────
   Same indicators, but layered with futures context (funding, OI).
   ═══════════════════════════════════════════════════════════════════════════════ */

export const ORDER_FLOW_FUTURES_TEMPLATES = [
  {
    name: 'OF + Funding Long',
    desc: 'Negative funding + bullish CVD + buy aggression',
    buy: [
      { ind: 'FundingRate',    op: '<', val: '-0.005' },
      { ind: 'CVD_EMA',        params: { period: 14 },  op: '>', val: '0',   combinator: 'AND' },
      { ind: 'AggressorRatio', params: { period: 100 }, op: '>', val: '0.6', combinator: 'AND' },
    ],
    sell: [
      { ind: 'FundingRate', op: '>', val: '0.02' },
      { ind: 'CVD',         op: '<', cmp: 'CVD_EMA', cmpParams: { period: 14 }, combinator: 'OR' },
    ],
  },

  {
    name: 'OI + Aggressor Continuation',
    desc: 'Rising OI + heavy buy market volume = real money chasing',
    buy: [
      { ind: 'OI_Delta',        params: { period: 5 },   op: '>', val: '3' },
      { ind: 'AggressorRatio',  params: { period: 100 }, op: '>', val: '0.6', combinator: 'AND' },
      { ind: 'BuyVolumeChange', params: { period: 5 },   op: '>', val: '15',  combinator: 'AND' },
    ],
    sell: [
      { ind: 'OI_Delta',        params: { period: 5 }, op: '<', val: '-3' },
      { ind: 'AggressorRatio',  params: { period: 100 }, op: '<', val: '0.4', combinator: 'OR' },
    ],
  },

  {
    name: 'Absorption + Liq Distance',
    desc: 'Buy absorption with safe liquidation distance',
    buy: [
      { ind: 'Absorption',          params: { lookback: 5, minDelta: 1000 },           op: '>', val: '0.7' },
      { ind: 'Delta',                                                                   op: '<', val: '0',  combinator: 'AND' },
      { ind: 'LiquidationDistance', params: { leverage: 10, maintRate: 0.004 },        op: '>', val: '15', combinator: 'AND' },
    ],
    sell: [
      { ind: 'CVD', op: '<', cmp: 'CVD_EMA', cmpParams: { period: 14 } },
    ],
  },
];


/* ═══════════════════════════════════════════════════════════════════════════════
   ORDER FLOW TEMPLATES — HEDGE (futures, both sides)
   ───────────────────────────────────────────────────────────────────────────────
   This is the closest legitimate version of the user's "pre-positioned both
   directions, kill the wrong one" idea. Only works on futures with hedge mode
   because it requires actual two-sided exposure.
   ═══════════════════════════════════════════════════════════════════════════════ */

export const ORDER_FLOW_HEDGE_TEMPLATES = [
  {
    name: 'Pre-Position Both Sides',
    desc: 'Open long & short simultaneously on consolidation, close the loser fast on flow shift',
    buy: [
      { ind: 'PriorBarMove',       params: { lookback: 5 },  op: '>', val: '30' },
      { ind: 'ConsolidationRange', params: { period: 10 },   op: '<', val: '8',  combinator: 'AND' },
      { ind: 'Price',                                         op: '<=', cmp: 'ConsolidationLow', cmpParams: { period: 10 }, combinator: 'AND' },
    ],
    sell: [
      // Close long when bearish flow confirms
      { ind: 'AggressorRatio',   params: { period: 100 }, op: '<', val: '0.4' },
      { ind: 'SellVolumeChange', params: { period: 5 },   op: '>', val: '10', combinator: 'AND' },
    ],
    shortOpen: [
      { ind: 'PriorBarMove',       params: { lookback: 5 },  op: '>',  val: '30' },
      { ind: 'ConsolidationRange', params: { period: 10 },   op: '<',  val: '8',  combinator: 'AND' },
      { ind: 'Price',                                         op: '>=', cmp: 'ConsolidationHigh', cmpParams: { period: 10 }, combinator: 'AND' },
    ],
    shortClose: [
      // Close short when bullish flow confirms
      { ind: 'AggressorRatio',  params: { period: 100 }, op: '>', val: '0.6' },
      { ind: 'BuyVolumeChange', params: { period: 5 },   op: '>', val: '10', combinator: 'AND' },
    ],
  },

  {
    name: 'Dual CVD Hedge',
    desc: 'Long when CVD breaks up, short when it breaks down — close on reversal',
    buy: [
      { ind: 'CVD',          op: '>', cmp: 'CVD_EMA', cmpParams: { period: 14 } },
      { ind: 'DeltaMomentum',params: { period: 5 }, op: '>', val: '0', combinator: 'AND' },
    ],
    sell: [
      { ind: 'CVD', op: '<', cmp: 'CVD_EMA', cmpParams: { period: 14 } },
    ],
    shortOpen: [
      { ind: 'CVD',          op: '<', cmp: 'CVD_EMA', cmpParams: { period: 14 } },
      { ind: 'DeltaMomentum',params: { period: 5 }, op: '<', val: '0', combinator: 'AND' },
    ],
    shortClose: [
      { ind: 'CVD', op: '>', cmp: 'CVD_EMA', cmpParams: { period: 14 } },
    ],
  },

  {
    name: 'Whale Flow Hedge',
    desc: 'Follow large prints in both directions',
    buy: [
      { ind: 'WhaleNetFlow', params: { period: 50, minSize: 250000 }, op: '>', val: '0' },
    ],
    sell: [
      { ind: 'WhaleNetFlow', params: { period: 50, minSize: 250000 }, op: '<', val: '0' },
    ],
    shortOpen: [
      { ind: 'WhaleNetFlow', params: { period: 50, minSize: 250000 }, op: '<', val: '0' },
    ],
    shortClose: [
      { ind: 'WhaleNetFlow', params: { period: 50, minSize: 250000 }, op: '>', val: '0' },
    ],
  },
];


/* ═══════════════════════════════════════════════════════════════════════════════
   AI PROMPTS — order flow flavored
   ═══════════════════════════════════════════════════════════════════════════════ */

export const AI_ORDER_FLOW_PROMPTS = [
  'Buy when CVD is rising and aggressor ratio is above 0.6',
  'Enter long after a 30% pump when price retests consolidation low with positive delta',
  'Fade extreme aggressor ratio: buy below 0.25, sell above 0.7',
  'Follow whale buys when net flow is positive and price is above VWAP',
  'Buy absorption: heavy sell delta but price holds, then bounce',
  'Exit long when sell volume increases 10% and aggressor ratio flips below 0.45',
  'Only chase breakouts when CVD EMA confirms — skip if delta diverges',
];


/* ═══════════════════════════════════════════════════════════════════════════════
   HELPERS for integration with StrategyBuilder
   ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Merge order flow indicators into an existing registry (CANDLE or FUTURES).
 * Use in the INDICATORS useMemo.
 */
export const withOrderFlow = (baseRegistry) => ({
  ...baseRegistry,
  ...ORDER_FLOW_INDICATORS,
});

/**
 * Pick the right order flow template set for the active mode.
 * Returns [] when order flow is disabled or in tick mode.
 */
export const getOrderFlowTemplates = ({ orderFlowEnabled, isTickMode, isFutures, isHedge }) => {
  if (!orderFlowEnabled || isTickMode) return [];
  if (isHedge)   return ORDER_FLOW_HEDGE_TEMPLATES;
  if (isFutures) return ORDER_FLOW_FUTURES_TEMPLATES;
  return ORDER_FLOW_SPOT_TEMPLATES;
};